import { supabase } from './supabase';

export type DSRVariant = 'lite' | 'standard' | 'full';

export interface ProjectListRow {
  id: string;
  name: string;
  code: string;
  operator_name: string | null;
  rig_name: string | null;
  well_name: string | null;
  operational_phase: string | null;
  water_depth_ft: number | null;
  is_active: boolean;
  dsr_variant: DSRVariant;
  division_id: string;
  business_unit_name: string | null;
  division_name: string | null;
  active_ssr_count: number;
}

export interface ProjectFormInput {
  name: string;
  code: string;
  division_id: string;
  operator_name: string | null;
  rig_name: string | null;
  well_name: string | null;
  operational_phase: string | null;
  water_depth_ft: number | null;
  is_active: boolean;
  dsr_variant: DSRVariant;
  location_id: string | null;
}

interface BusinessUnitRow {
  id: string;
  name: string;
}

interface DivisionRow {
  id: string;
  business_unit_id: string;
  name: string;
}

interface LocationRow {
  id: string;
  name: string;
}

export async function listProjects(tenantId: string): Promise<ProjectListRow[]> {
  const { data, error } = await supabase
    .from('projects')
    .select(
      `id, name, code, operator_name, rig_name, well_name,
       operational_phase, water_depth_ft, is_active, dsr_variant, division_id,
       divisions!inner (
         name,
         business_units!inner ( name )
       )`,
    )
    .eq('tenant_id', tenantId)
    .eq('is_deleted', false)
    .order('name');

  if (error) throw error;

  const projects = (data ?? []) as unknown as Array<{
    id: string;
    name: string;
    code: string;
    operator_name: string | null;
    rig_name: string | null;
    well_name: string | null;
    operational_phase: string | null;
    water_depth_ft: number | null;
    is_active: boolean;
    dsr_variant: DSRVariant;
    division_id: string;
    divisions: {
      name: string;
      business_units: { name: string };
    } | null;
  }>;

  // Fetch active SSR counts per project in one round-trip
  const { data: assignments, error: aerr } = await supabase
    .from('user_project_assignments')
    .select('project_id, user_id, users!inner(role, is_active, is_deleted)')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false);

  if (aerr) throw aerr;

  const ssrCount = new Map<string, number>();
  type AssignmentRow = {
    project_id: string;
    users: { role: string; is_active: boolean; is_deleted: boolean };
  };
  for (const row of (assignments ?? []) as unknown as AssignmentRow[]) {
    if (
      row.users &&
      row.users.role === 'ssr' &&
      row.users.is_active &&
      !row.users.is_deleted
    ) {
      ssrCount.set(row.project_id, (ssrCount.get(row.project_id) ?? 0) + 1);
    }
  }

  return projects.map((p) => ({
    id: p.id,
    name: p.name,
    code: p.code,
    operator_name: p.operator_name,
    rig_name: p.rig_name,
    well_name: p.well_name,
    operational_phase: p.operational_phase,
    water_depth_ft: p.water_depth_ft,
    is_active: p.is_active,
    dsr_variant: p.dsr_variant,
    division_id: p.division_id,
    business_unit_name: p.divisions?.business_units?.name ?? null,
    division_name: p.divisions?.name ?? null,
    active_ssr_count: ssrCount.get(p.id) ?? 0,
  }));
}

export async function getBusinessUnits(tenantId: string): Promise<BusinessUnitRow[]> {
  const { data, error } = await supabase
    .from('business_units')
    .select('id, name')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false)
    .order('name');
  if (error) throw error;
  return data ?? [];
}

export async function getDivisions(tenantId: string): Promise<DivisionRow[]> {
  const { data, error } = await supabase
    .from('divisions')
    .select('id, business_unit_id, name')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false)
    .order('name');
  if (error) throw error;
  return data ?? [];
}

export async function getLocations(tenantId: string): Promise<LocationRow[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('id, name')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .eq('is_deleted', false)
    .order('name');
  if (error) throw error;
  return data ?? [];
}

export async function getProjectLocation(projectId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('project_locations')
    .select('location_id')
    .eq('project_id', projectId)
    .eq('is_active', true)
    .eq('is_deleted', false)
    .maybeSingle();
  if (error) throw error;
  return data?.location_id ?? null;
}

export async function createProject(
  tenantId: string,
  userId: string,
  input: ProjectFormInput,
): Promise<string> {
  const { data, error } = await supabase
    .from('projects')
    .insert({
      tenant_id: tenantId,
      division_id: input.division_id,
      name: input.name,
      code: input.code,
      operator_name: input.operator_name,
      rig_name: input.rig_name,
      well_name: input.well_name,
      operational_phase: input.operational_phase,
      water_depth_ft: input.water_depth_ft,
      is_active: input.is_active,
      dsr_variant: input.dsr_variant,
      created_by: userId,
    })
    .select('id')
    .single();

  if (error) throw error;
  const newId = data.id as string;

  if (input.location_id) {
    const { error: loErr } = await supabase.from('project_locations').insert({
      tenant_id: tenantId,
      project_id: newId,
      location_id: input.location_id,
      is_active: true,
      created_by: userId,
    });
    if (loErr) throw loErr;
  }

  return newId;
}

export async function updateProject(
  tenantId: string,
  userId: string,
  projectId: string,
  input: ProjectFormInput,
  previousLocationId: string | null,
): Promise<void> {
  // Why (variant on update — ARCH-A2): updating projects.dsr_variant only
  // affects DSRs created AFTER this point. Existing DSRs keep their frozen
  // variant on dsr_reports.variant. The form copy below the Select
  // explains this to the operator at edit time.
  const { error } = await supabase
    .from('projects')
    .update({
      division_id: input.division_id,
      name: input.name,
      code: input.code,
      operator_name: input.operator_name,
      rig_name: input.rig_name,
      well_name: input.well_name,
      operational_phase: input.operational_phase,
      water_depth_ft: input.water_depth_ft,
      is_active: input.is_active,
      dsr_variant: input.dsr_variant,
    })
    .eq('id', projectId);

  if (error) throw error;

  // Reconcile project_locations: only one row can be active per project
  // (partial unique index enforces this).
  if (input.location_id !== previousLocationId) {
    if (previousLocationId) {
      const { error: deErr } = await supabase
        .from('project_locations')
        .update({ is_active: false, effective_to: new Date().toISOString() })
        .eq('project_id', projectId)
        .eq('is_active', true)
        .eq('is_deleted', false);
      if (deErr) throw deErr;
    }
    if (input.location_id) {
      const { error: loErr } = await supabase.from('project_locations').insert({
        tenant_id: tenantId,
        project_id: projectId,
        location_id: input.location_id,
        is_active: true,
        created_by: userId,
      });
      if (loErr) throw loErr;
    }
  }
}

export async function getProjectsForInvite(tenantId: string) {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, code')
    .eq('tenant_id', tenantId)
    .eq('is_deleted', false)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return data ?? [];
}
